import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Plus, Download, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CardStats } from '@components/ui';

/**
 * MaterialPageLayout - Malzeme sayfaları için ortak layout
 *
 * @param {string} title - Sayfa başlığı
 * @param {string} subtitle - Alt başlık (kategori adı)
 * @param {React.ReactNode} icon - Başlık ikonu (Lucide icon component)
 * @param {string} iconBgColor - İkon arka plan rengi (Tailwind class)
 * @param {string} backLink - Geri linki URL
 * @param {Array} stats - Özet kartları verisi [{title, value, icon, color}]
 * @param {Function} onAddClick - Yeni kayıt butonu tıklaması
 * @param {Function} onExportClick - Export butonu tıklaması
 * @param {React.ReactNode} headerFilter - Header satırında gösterilecek filtre (kategori select vb.)
 * @param {React.ReactNode} children - Ana içerik (tablo veya kartlar)
 */
export default function MaterialPageLayout({
    title,
    subtitle,
    icon: Icon,
    iconBgColor = 'bg-primary',
    backLink,
    stats = [],
    onAddClick,
    onExportClick,
    headerFilter,
    children,
}) {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const projectName = showAllConstructions
        ? 'Tüm İnşaatlar'
        : selectedConstruction?.name || 'Seçili proje yok';

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                    {/* Geri Butonu */}
                    {backLink && (
                        <Link
                            to={backLink}
                            className="p-2 rounded-lg bg-bg-tertiary hover:bg-bg-hover transition-colors"
                        >
                            <ChevronLeft size={20} className="text-text-secondary" />
                        </Link>
                    )}

                    {/* Başlık */}
                    <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center', iconBgColor)}>
                        {Icon && <Icon size={24} className="text-white" />}
                    </div>
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold text-text-primary">{title}</h1>
                        <p className="text-sm text-text-secondary">
                            {subtitle && <span className="text-text-muted">{subtitle} → </span>}
                            {projectName}
                        </p>
                    </div>
                </div>

                {/* Aksiyon Butonları + Filtre */}
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Kategori Filtresi — header'da belirgin */}
                    {headerFilter}

                    {onExportClick && (
                        <button
                            onClick={onExportClick}
                            className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary hover:bg-bg-hover rounded-lg transition-colors text-text-secondary"
                        >
                            <Download size={18} />
                            <span className="hidden sm:inline text-sm">Dışa Aktar</span>
                        </button>
                    )}
                    {onAddClick && (
                        <button
                            onClick={onAddClick}
                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors font-medium"
                        >
                            <Plus size={18} />
                            <span className="text-sm">Yeni Kayıt</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Özet Kartları */}
            {stats.length > 0 && (
                <div className={clsx(
                    'grid gap-4',
                    stats.length === 4 && 'grid-cols-2 md:grid-cols-4',
                    stats.length === 3 && 'grid-cols-1 md:grid-cols-3',
                    stats.length === 2 && 'grid-cols-2',
                    stats.length === 1 && 'grid-cols-1',
                )}>
                    {stats.map((stat, index) => (
                        <CardStats
                            key={index}
                            title={stat.title}
                            value={stat.value}
                            icon={stat.icon}
                            color={stat.color}
                            trend={stat.trend}
                            suffix={stat.suffix}
                        />
                    ))}
                </div>
            )}

            {/* Ana İçerik */}
            <div className="bg-bg-secondary rounded-xl border border-border-light overflow-hidden">
                {children}
            </div>
        </div>
    );
}
